import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Activity, Boxes, CircleHelp, Clock3, Cpu, HeartPulse, Info, LayoutDashboard, MonitorCog, RefreshCw, Rocket, Server, ShieldAlert, ShieldCheck } from 'lucide-react'
import './style.css'

const SOURCES = [
  ['nodes', '/api/labs/pilot'],
  ['containers', '/api/podman/snapshot'],
  ['docker', '/api/docker/snapshot'],
  ['processes', '/api/processes/snapshot'],
  ['services', '/api/deploy/services'],
  ['monitor', '/api/deploy/monitor'],
  ['devCluster', '/api/orch/k8s/snapshot'],
]

function headers() {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function errorMessage(code) {
  return ({
    LOGIN_REQUIRED: '로그인이 필요합니다.', INVALID_SESSION: '로그인이 만료됐습니다.',
    ADMIN_REQUIRED: '스카이넷 관리자 권한이 필요합니다.',
    AUTH_NOT_CONFIGURED: '관리자 인증 설정이 준비되지 않았습니다.',
    AUTH_UNAVAILABLE: '관리자 권한을 확인할 수 없습니다.',
    INVENTORY_UNAVAILABLE: '에이전트 목록을 읽을 수 없습니다.',
    PODMAN_UNAVAILABLE: 'API 서버의 Podman 상태를 읽을 수 없습니다.',
    DOCKER_UNAVAILABLE: 'API 서버의 Docker 상태를 읽을 수 없습니다.',
    PROCESS_MANAGER_NOT_FOUND: 'API 서버의 PM2를 찾을 수 없습니다.',
    PROCESS_MANAGER_UNAVAILABLE: 'PM2 실행 목록을 읽을 수 없습니다.',
    CATALOG_UNAVAILABLE: '서비스 등록 목록을 읽을 수 없습니다.',
    MONITOR_UNAVAILABLE: '서비스 실행 상태를 읽을 수 없습니다.',
    CLUSTER_UNAVAILABLE: '헤츠너 개발 클러스터 연결 또는 조회 권한을 확인해 주세요.',
  })[code] || '현재 상태를 읽을 수 없습니다.'
}

function time(value) {
  if (!value) return '보고 없음'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '시각 확인 불가' : date.toLocaleString('ko-KR')
}

function gib(value) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? `${(n / (1024 ** 3)).toFixed(1)} GiB` : '미보고'
}

async function readSource(url, signal) {
  const response = await fetch(url, { headers: headers(), credentials: 'include', signal })
  let body
  try { body = await response.json() }
  catch { throw Error('현재 상태를 읽을 수 없습니다.') }
  if (!response.ok) throw Error(errorMessage(body.code))
  return body
}

function SourceError({ label, error }) {
  return error ? <p className="ops-error" role="alert"><ShieldAlert size={16} />{label}: {error}</p> : null
}

function observedProcesses(snapshot) {
  if (!snapshot) return { count: null, incomplete: false, items: [] }
  const hosts = Array.isArray(snapshot.hosts) ? snapshot.hosts
    : [{ processes: snapshot.processes, truncated: Array.isArray(snapshot.processes)
      && snapshot.processes.length >= snapshot.processLimit }]
  const complete = hosts.filter(host => !host.unavailable && !host.stale
    && !host.truncated && host.verified !== false && Array.isArray(host.processes))
  const items = complete.flatMap(host => host.processes.filter(item =>
    item.status === 'online' && item.managerModule !== true)
    .map(item => ({ name: item.name, host: host.hostname || '운영 서버' })))
  return { count: complete.length ? items.length : null,
    incomplete: complete.length !== hosts.length, items }
}

function SourceStatus({ label, available, error, loading }) {
  const state = available ? '조회됨' : error ? '조회 실패' : loading ? '확인 중' : '미확인'
  return <span className={`ops-source ${available ? 'ops-source-ok' : error ? 'ops-source-error' : ''}`}>
    <span className="ops-dot" aria-hidden="true" />{label} {state}
  </span>
}

function PanelTitle({ icon: Icon, children }) {
  return <h2><span className="ops-panel-icon"><Icon size={19} aria-hidden="true" /></span>{children}</h2>
}

const UNKNOWN = { tone: 'unknown', state: '확인 불가' }

function nodeSignal(snapshot) {
  if (!snapshot || !Array.isArray(snapshot.peers) || !snapshot.peers.length) return UNKNOWN
  const online = snapshot.peers.filter(peer => peer.status === 'online').length
  if (snapshot.peers.some(peer => !peer.status)) return { tone: 'unknown', state: '일부 상태 미확인' }
  if (online === snapshot.peers.length) return { tone: 'good', state: '모두 연결' }
  return online === 0 ? { tone: 'danger', state: '연결된 에이전트 없음' }
    : { tone: 'caution', state: '일부 연결 끊김' }
}

function containerTone(item) {
  const state = String(item.state || '').toLowerCase()
  const status = String(item.status || '').toLowerCase()
  if (/(unhealthy|dead|error|failed)/.test(`${state} ${status}`)) return 'danger'
  if (state === 'running' || (!state && /^up\b/.test(status))) return 'good'
  if (state || status) return 'caution'
  return 'unknown'
}

function containerSignal(snapshot) {
  if (snapshot?.counts?.total === 0 && snapshot.truncated === false) return { tone: 'unknown', state: '등록 없음' }
  if (!snapshot || !Array.isArray(snapshot.containers) || !snapshot.containers.length) return UNKNOWN
  if (snapshot.containers.some(item => containerTone(item) === 'danger')) return { tone: 'danger', state: '오류 상태 있음' }
  if (Number.isFinite(snapshot.containerLimit) && snapshot.containers.length >= snapshot.containerLimit)
    return { tone: 'unknown', state: '목록 일부만 조회' }
  if (snapshot.containers.some(item => containerTone(item) === 'unknown')) return UNKNOWN
  if (snapshot.containers.some(item => containerTone(item) === 'caution'))
    return { tone: 'caution', state: '실행 외 상태 있음' }
  return { tone: 'good', state: '모두 실행 중' }
}

function serviceSignal(snapshot) {
  if (!snapshot || !Array.isArray(snapshot.services)) return UNKNOWN
  const environments = snapshot.services.flatMap(service => service.environments || [])
  if (!environments.length) return UNKNOWN
  if (environments.some(item => item.status === 'drift')) return { tone: 'danger', state: '지정·실행 불일치' }
  if (snapshot.observation?.available !== true || environments.some(item => !item.status ||
    item.status === 'unverified' || item.status === 'unset')) return { tone: 'unknown', state: '일부 상태 미확인' }
  if (environments.some(item => item.status === 'stopped' || item.status === 'maintenance'))
    return { tone: 'caution', state: '중단·점검 있음' }
  return environments.every(item => item.status === 'operating')
    ? { tone: 'good', state: '등록 대상 실행 중' } : UNKNOWN
}

function devClusterSignal(snapshot) {
  if (!snapshot || snapshot.readOnly !== true || snapshot.namespace !== 'antsnest'
    || !Array.isArray(snapshot.nodes) || !snapshot.nodes.length || !snapshot.summary) return UNKNOWN
  const observedAt = Date.parse(snapshot.observedAt)
  if (!Number.isFinite(observedAt) || Math.abs(Date.now() - observedAt) > 180000)
    return { tone: 'unknown', state: '최근 상태 미확인' }
  const ready = snapshot.nodes.filter(node => node.ready === 'True').length
  if (!ready) return { tone: 'danger', state: '준비된 노드 없음' }
  if (snapshot.nodes.some(node => node.ready === 'False'))
    return { tone: 'danger', state: '노드 이상 있음' }
  if (ready < snapshot.nodes.length || snapshot.nodes.some(node =>
    (node.warnings || []).length || node.unhealthyPods > 0) || snapshot.summary.unassignedPods > 0)
    return { tone: 'caution', state: '일부 상태 주의' }
  return { tone: 'good', state: '개발 클러스터 정상' }
}

function TrafficSignal({ label, signal, detail, href, direct }) {
  return <article className={`ops-traffic-card ops-traffic-${signal.tone}`} aria-label={`${label}: ${signal.state}`}>
    <span className="ops-traffic-light" aria-hidden="true"><i /><i /><i /></span>
    <div><span className="ops-traffic-label">{label}</span><strong>{signal.state}</strong><small>{detail}</small></div>
    {direct ? <a href={href}>자세히 보기 →</a> : <Link to={href}>자세히 보기 →</Link>}
  </article>
}

export default function Operations() {
  const [data, setData] = useState({})
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [checkedAt, setCheckedAt] = useState(null)

  const load = useCallback(async (signal) => {
    setLoading(true)
    const results = await Promise.all(SOURCES.map(async ([key, url]) => {
      try { return [key, await readSource(url, signal), null] }
      catch (cause) { return [key, null, cause.name === 'AbortError' ? null : cause.message] }
    }))
    if (signal?.aborted) return
    setData(Object.fromEntries(results.map(([key, value]) => [key, value])))
    setErrors(Object.fromEntries(results.map(([key, , error]) => [key, error])))
    setCheckedAt(new Date())
    setLoading(false)
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    const interval = setInterval(() => load(controller.signal), 60000)
    return () => { clearInterval(interval); controller.abort() }
  }, [load])

  const peers = data.nodes?.peers || []
  const containers = data.containers?.containers || []
  const dockerContainers = data.docker?.containers || []
  const events = data.containers?.events || []
  const services = data.services?.services || []
  const processes = observedProcesses(data.processes)
  const runningContainers = containers.filter(item => containerTone(item) === 'good')
  const runningDocker = dockerContainers.filter(item => item.state === 'running')
  const devSnapshotFresh = data.devCluster?.readOnly === true
    && data.devCluster.namespace === 'antsnest'
    && Number.isFinite(Date.parse(data.devCluster.observedAt))
    && Math.abs(Date.now() - Date.parse(data.devCluster.observedAt)) <= 180000
  const devDeployments = devSnapshotFresh && Array.isArray(data.devCluster.deployments)
    ? data.devCluster.deployments.filter(item => Number(item.ready) > 0) : null
  const unregisteredContainers = data.containers && data.services
    ? containers.filter(item => /^[a-z0-9][a-z0-9_.-]{0,63}$/.test(item.name || '')
      && !services.some(service => service.prod_target === `podman:${item.name}`)) : []
  const monitorCount = data.monitor?.services?.reduce((count, service) => count + (service.environments?.length || 0), 0) || 0
  const devNodes = data.devCluster?.nodes || []
  const devReady = devNodes.filter(node => node.ready === 'True').length
  const traffic = [
    ['서버 관리 에이전트', nodeSignal(data.nodes), data.nodes ? `${peers.length}개 등록 · 물리 노드와 별도` : '에이전트 조회 실패·대기', '/labs'],
    ['로컬 컨테이너', containerSignal(data.containers), data.containers ? `${containers.length}개 조회 · API 서버 Podman` : '컨테이너 조회 실패·대기', '/containers'],
    ['Docker', containerSignal(data.docker), data.docker?.counts ? `전체 ${data.docker.counts.total} · 실행 ${data.docker.counts.running} · 중지 ${data.docker.counts.stopped}` : 'Docker 조회 실패·대기', '/containers'],
    ['서비스 실행', serviceSignal(data.monitor), data.monitor ? `${monitorCount}개 환경 대조 · 앱 건강검사 아님` : '실행 상태 조회 실패·대기', '/monitor'],
    ['헤츠너 개발 클러스터', devClusterSignal(data.devCluster), data.devCluster
      ? `준비 ${devReady}/${devNodes.length} 노드 · Pod ${data.devCluster.summary?.pods ?? '미확인'}개 · 관측 ${time(data.devCluster.observedAt)}`
      : '클러스터 조회 실패·대기', '/api/orch/k8s/console', true],
  ]
  const counts = [
    ['서버 관리 에이전트', data.nodes ? peers.length : '확인 불가', <Server size={20} />],
    ['온라인 에이전트', data.nodes ? peers.filter(peer => peer.status === 'online').length : '확인 불가', <Activity size={20} />],
    ['Podman 컨테이너', data.containers ? `${containers.length}${containers.length >= data.containers.containerLimit ? '+' : ''}` : '확인 불가', <Boxes size={20} />],
    ['Docker 컨테이너', data.docker?.counts ? data.docker.counts.total : '확인 불가', <Boxes size={20} />],
    ['실행 중 PM2 앱', processes.count === null ? '확인 불가' : `${processes.count}${processes.incomplete ? '+' : ''}`, <Activity size={20} />],
    ['배포 관리 구성', data.services ? services.length : '확인 불가', <Rocket size={20} />],
    ['컨테이너 등록 후보', data.containers && data.services ? unregisteredContainers.length : '확인 불가', <Rocket size={20} />],
  ]

  return <main className="ops-page">
    <header className="ops-header"><div className="ops-heading"><span className="ops-heading-icon"><MonitorCog size={28} aria-hidden="true" /></span><div><span className="console-eyebrow">INFRASTRUCTURE OVERVIEW</span><h1>인프라 현황</h1>
      <p>서버 관리 에이전트, 개발 클러스터 노드, 운영 컨테이너와 서비스 구성</p></div></div>
      <button type="button" onClick={() => load()} disabled={loading}><RefreshCw size={16} />{loading ? '확인 중…' : '새로고침'}</button>
    </header>
    <nav className="ops-tabs" aria-label="서비스 운영 메뉴">
      <Link to="/infrastructure" aria-current="page"><LayoutDashboard size={18} aria-hidden="true" />전체 현황</Link>
      <Link to="/monitor"><HeartPulse size={18} aria-hidden="true" />서비스 상태</Link>
      <Link to="/deploy"><Rocket size={18} aria-hidden="true" />서비스 등록·배포</Link>
      <Link to="/deploy/plan"><ShieldCheck size={18} aria-hidden="true" />무중단 배포 설계</Link>
      <Link to="/containers"><Clock3 size={18} aria-hidden="true" />컨테이너 이력</Link>
      <Link to="/ai-services"><Cpu size={18} aria-hidden="true" />AI 설계</Link>
    </nav>
    <div className="ops-body">
      <div className="ops-sources" aria-label="자료 조회 상태">
        <SourceStatus label="서버 관리 에이전트" available={Boolean(data.nodes)} error={errors.nodes} loading={loading} />
        <SourceStatus label="컨테이너" available={Boolean(data.containers)} error={errors.containers} loading={loading} />
        <SourceStatus label="Docker" available={Boolean(data.docker?.counts)} error={errors.docker} loading={loading} />
        <SourceStatus label="PM2 앱" available={Boolean(data.processes)} error={errors.processes} loading={loading} />
        <SourceStatus label="서비스" available={Boolean(data.services)} error={errors.services} loading={loading} />
        <SourceStatus label="실행 상태" available={Boolean(data.monitor)} error={errors.monitor} loading={loading} />
        <SourceStatus label="헤츠너 개발" available={Boolean(data.devCluster)} error={errors.devCluster} loading={loading} />
      </div>
      <p className="ops-time" role="status">마지막 화면 조회: {time(checkedAt)} · 자동 갱신 60초</p>
      <SourceError label="헤츠너 개발 클러스터" error={errors.devCluster} />
      <section className="ops-traffic" aria-label="운영 상태 신호등">
        <div className="ops-traffic-head"><h2>운영 상태 신호등</h2><p>초록 정상 · 노랑 주의 · 빨강 확인된 이상 · 회색 확인 불가</p></div>
        <div className="ops-traffic-grid">{traffic.map(([label, signal, detail, href, direct]) =>
          <TrafficSignal key={label} label={label} signal={signal} detail={detail} href={href} direct={direct} />)}</div>
        <p className="ops-traffic-note">조회 성공은 서비스 건강 보증이 아닙니다. 등록 서비스의 상태는 지정·실행 대조이며 실제 요청 건강검사는 포함하지 않습니다.</p>
      </section>
      <section className="ops-runtime" aria-label="실행 중 발견한 서비스 유형별 현황">
        <div className="ops-runtime-head"><h2>실행 중 발견한 서비스</h2><p>서버별 실행 목록 · 배포 관리 등록과 별도</p></div>
        <div className="ops-runtime-grid">
          <article className="ops-runtime-card" aria-label="운영 서버 PM2 실행 서비스">
            <h3><Activity size={20} aria-hidden="true" />운영 서버 · PM2</h3>
            <strong>{processes.count === null ? '조회 불가' : `${processes.count}${processes.incomplete ? '+' : ''}개 실행 확인`}</strong>
            <small>프로세스 관리자의 online 상태 · HTTP 건강검사는 별도</small>
            {processes.items.length > 0 && <ul>{processes.items.map((item, index) =>
              <li key={`${item.host}-${item.name}-${index}`}><span className="ops-dot" aria-hidden="true" />{item.name}<small>{item.host}</small></li>)}</ul>}
            {processes.incomplete && <small>일부 호스트는 조회되지 않아 합계가 더 클 수 있습니다.</small>}
          </article>
          <article className="ops-runtime-card" aria-label="운영 서버 Podman 실행 컨테이너">
            <h3><Boxes size={20} aria-hidden="true" />운영 서버 · Podman</h3>
            <strong>{data.containers ? `${runningContainers.length}${containers.length >= data.containers.containerLimit ? '+' : ''}개 실행 확인` : '조회 불가'}</strong>
            <small>API 서버에서 확인된 컨테이너 · 저장된 이미지와 별도</small>
            {runningContainers.length > 0 && <ul>{runningContainers.map((item, index) =>
              <li key={item.id || `${item.name}-${index}`}><span className="ops-dot" aria-hidden="true" />{item.name || item.id || '이름 없음'}<small>{item.image || '이미지 미확인'}</small></li>)}</ul>}
          </article>
          <article className="ops-runtime-card" aria-label="운영 서버 Docker 실행 컨테이너">
            <h3><Boxes size={20} aria-hidden="true" />운영 서버 · Docker</h3>
            <strong>{data.docker?.counts ? `${data.docker.counts.running}개 실행 확인` : '조회 불가'}</strong>
            <small>{data.docker?.counts ? `전체 ${data.docker.counts.total} · 중지 ${data.docker.counts.stopped} · 기타 ${data.docker.counts.other}` : 'Docker 엔진 조회 실패·대기'}</small>
            <SourceError label="Docker" error={errors.docker} />
            {runningDocker.length > 0 && <ul>{runningDocker.map(item =>
              <li key={item.id}><span className="ops-dot" aria-hidden="true" />{item.name}<small>{item.image}</small></li>)}</ul>}
            <Link to="/containers">Docker 전체 목록 →</Link>
          </article>
          <article className="ops-runtime-card" aria-label="헤츠너 개발 Kubernetes 준비된 배포">
            <h3><Server size={20} aria-hidden="true" />헤츠너 개발 · Kubernetes</h3>
            <strong>{devDeployments === null ? '최근 배포 목록 확인 불가' : `${devDeployments.length}종 준비 확인`}</strong>
            <small>{devSnapshotFresh ? `Pod ${data.devCluster.summary?.pods ?? '미확인'}개 관측 · 배포의 준비된 복제본 기준` : '최근 클러스터 상태가 없어 실행 수를 단정하지 않습니다.'}</small>
            {devDeployments?.length > 0 && <ul>{devDeployments.map(item =>
              <li key={`${item.namespace}/${item.name}`}><span className="ops-dot" aria-hidden="true" />{item.name}<small>준비 {item.ready}/{item.desired}</small></li>)}</ul>}
          </article>
        </div>
      </section>
      <div className="ops-stats">{counts.map(([label, value, icon], index) => <article key={label} aria-label={`운영 통계 ${label}: ${value}`} className={`ops-stat ops-stat-${index}`}>
        <span className="ops-stat-icon" aria-hidden="true">{icon}</span><span className="ops-stat-label">{label}</span><strong>{value}</strong></article>)}</div>
      {data.services && data.processes && processes.count > 0 && services.length === 0 &&
        <aside className="ops-discovery" aria-label="실행 중 PM2 앱과 서비스 등록 안내">
          <div className="ops-discovery-icons" aria-hidden="true"><Activity size={24} /><span>→</span><Rocket size={24} /></div>
          <div><strong>운영 PM2 앱 {processes.count}{processes.incomplete ? '개 이상' : '개'} 실행 확인</strong>
            <p>배포 관리 구성은 0개지만 서비스가 없는 것은 아닙니다. 실행 목록은 자동 등록되지 않아 소스 저장소와 개발·운영 대상을 확인해 연결해야 합니다.</p></div>
          <Link to="/deploy">PM2 서비스 등록 화면 →</Link>
        </aside>}
      {unregisteredContainers.length > 0 && <aside className="ops-discovery" aria-label="컨테이너와 서비스 등록 안내">
        <div className="ops-discovery-icons" aria-hidden="true"><Boxes size={24} /><span>→</span><Rocket size={24} /></div>
        <div><strong>컨테이너 {unregisteredContainers.length}개는 조회됐지만 서비스로 등록되지 않았습니다</strong><p>실행 발견과 서비스 등록은 별개입니다. 운영 대상은 채울 수 있지만 소스 저장소와 개발 대상은 확인 후 등록해야 합니다.</p></div>
        <Link to="/deploy">서비스 등록 화면 →</Link>
      </aside>}
      <section className="ops-panel"><div className="ops-panel-head"><PanelTitle icon={Server}>서버 관리 에이전트</PanelTitle><Link to="/labs">역할 연결 관리 →</Link></div>
        <SourceError label="서버 관리 에이전트" error={errors.nodes} />
        {data.nodes && (peers.length ? <div className="ops-list">{peers.map(peer => <article key={peer.peer_id} className="ops-item">
          <div><strong className="ops-item-name"><Server size={16} aria-hidden="true" />{peer.hostname || `에이전트 ${peer.peer_id}`}</strong><span className={`ops-badge ${peer.status === 'online' ? 'ops-online' : peer.status ? 'ops-danger' : ''}`}><span className="ops-dot" aria-hidden="true" />{peer.status || '상태 미확인'}</span></div>
          <p>VPC {peer.mesh_id} · 마지막 연결 {time(peer.last_seen)}</p>
          <p>CPU {peer.cpu_cores || '미보고'} · 메모리 {gib(peer.memory_bytes)} · 루트 디스크 {gib(peer.root_disk_bytes)}</p>
          <small>사양 보고 {time(peer.hardware_seen_at)}</small>
        </article>)}</div> : <p className="ops-empty"><CircleHelp size={18} aria-hidden="true" /><span>등록된 에이전트가 없습니다. <Link to="/network">네트워크 관리</Link>에서 연결하세요.</span></p>)}
      </section>
      <div className="ops-columns"><section className="ops-panel"><div className="ops-panel-head"><PanelTitle icon={Boxes}>컨테이너 현황</PanelTitle><Link to="/monitor">서비스 운영 현황 →</Link></div>
        <SourceError label="컨테이너" error={errors.containers} />
        {data.containers && (containers.length ? <div className="ops-list">{containers.map((item, index) => <article key={item.id || index} className="ops-item">
          <div><strong className="ops-item-name"><Boxes size={16} aria-hidden="true" />{item.name || item.id || '이름 없음'}</strong>
            <span className={`ops-badge ops-${containerTone(item)}`}><span className="ops-dot" aria-hidden="true" />{item.status || item.state || '상태 미확인'}</span></div>
          <p>{item.image || '이미지 미확인'}</p>
          {unregisteredContainers.includes(item) && <Link to={`/deploy?prodTarget=${encodeURIComponent(`podman:${item.name}`)}`}>서비스 등록 양식에 연결 →</Link>}
        </article>)}</div> : <p className="ops-empty">조회된 컨테이너가 없습니다.</p>)}
      </section><section className="ops-panel"><div className="ops-panel-head"><PanelTitle icon={Clock3}>최근 7일 운영 이력</PanelTitle><Link to="/containers">전체 보기 →</Link></div>
        <SourceError label="운영 이력" error={errors.containers} />
        {data.containers && (data.containers.historyState !== 'available' ? <p className="ops-empty">이벤트 기록이 {data.containers.historyState === 'disabled' ? '비활성화됐습니다.' : '조회 불가 상태입니다.'}</p>
          : events.length ? <div className="ops-list">{events.slice(0, 8).map((event, index) => <article key={`${event.time}-${index}`} className="ops-item"><strong>{event.action || event.type || '이벤트'}</strong><p>{event.name || '대상 미확인'} · {time(event.time)}</p></article>)}</div>
            : <p className="ops-empty">조회된 최근 이벤트가 없습니다. 작업이 없었다는 증거는 아닙니다.</p>)}
      </section></div>
      <section className="ops-panel"><div className="ops-panel-head"><PanelTitle icon={Rocket}>배포 관리에 등록한 구성</PanelTitle><Link to="/deploy">서비스 등록 관리 →</Link></div>
        <SourceError label="서비스" error={errors.services} />
        {data.services && (services.length ? <div className="ops-list">{services.map(service => <article key={service.service_id} className="ops-item"><strong className="ops-item-name"><Rocket size={16} aria-hidden="true" />{service.display_name}</strong>
          <p>개발 {service.dev_target} · 운영 {service.prod_target}</p><small className="ops-limited">구성 등록만 완료 · 이미지 배포 상태는 아직 수집하지 않습니다.</small></article>)}</div>
          : <p className="ops-empty">배포 관리에 등록한 구성이 없습니다. 위 실행 현황과는 별도입니다.</p>)}
      </section>
      <details className="ops-disclosure"><summary>상태 판정과 조회 범위 안내</summary>      <p className="ops-note"><Info size={19} aria-hidden="true" /><span>실제 조회값만 표시합니다. 운영 PM2·Podman·Docker와 개발 Kubernetes는 서로 다른 실행 방식입니다. Ansible 구성 여부는 PM2 조회만으로 판정하지 않습니다. 배포 관리 등록 수는 실행 중인 서비스 수가 아닙니다. 이 화면에서 배포·재시작·삭제는 실행하지 않습니다.</span></p>
</details>
    </div>
  </main>
}
